import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'

export interface SchoolStudentSummary {
  studentId: string
  joinedAt: string
  fullName: string | null
  email: string
  instructorId: string | null
  instructorName: string | null
  relationshipId: string | null
  packageId: string | null
  packageName: string | null
  lessonsCompleted: number
  lessonsPending: number
}

interface UseSchoolStudentsReturn {
  students: SchoolStudentSummary[]
  loading: boolean
  error: string | null
  refresh: () => void
}

/**
 * Lists the ACTIVE students of the school currently selected in the school-admin portal
 * (SelectedSchoolContext, shared with useInstructors so both views stay consistent when
 * switching schools).
 *
 * The school id used here is UI state, not a trust boundary — see useInstructors.ts for
 * the same note in full.
 *
 * Two independent sources, unioned by student id (migration 074): a school-initiated
 * direct enrollment (school_student_memberships, migration 042 — the mechanism this
 * product's actual usage has never gone through in practice) and the real path students
 * arrive by, an instructor (employee or contractor) enrolling their own student and
 * attributing them to this school (instructor_student_relationships.school_id, same
 * source Tracking Sheets already reads, migration 069). Profile reads for both rely on
 * is_school_admin_for_student() recognizing either path (migration 074).
 */
export function useSchoolStudents(): UseSchoolStudentsReturn {
  const { selectedSchoolId } = useSelectedSchool()
  const [students, setStudents] = useState<SchoolStudentSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStudents = useCallback(async () => {
    if (!selectedSchoolId) { setStudents([]); setLoading(false); return }
    setLoading(true)
    setError(null)

    try {
      const [membershipsRes, relationshipsRes] = await Promise.all([
        supabase
          .from('school_student_memberships')
          .select('student_id, joined_at')
          .eq('school_id', selectedSchoolId)
          .eq('status', 'active'),
        supabase
          .from('instructor_student_relationships')
          .select('id, student_id, started_at, instructor_id, package_id, school_packages(name)')
          .eq('school_id', selectedSchoolId)
          .eq('status', 'active'),
      ])

      if (membershipsRes.error || relationshipsRes.error) { setError('Could not load students.'); return }

      // Union by student id, keeping the EARLIER of the two "since" dates when a student
      // somehow has both (school-initiated AND instructor-attributed) — "since" means
      // since they first became this school's student, not since whichever row happened
      // to be read first. instructorId only ever comes from the relationship path —
      // school_student_memberships has no instructor attached at all.
      const sinceByStudent = new Map<string, string>()
      const instructorByStudent = new Map<string, string>()
      const relationshipByStudent = new Map<string, { id: string; packageId: string | null; packageName: string | null }>()
      for (const m of membershipsRes.data ?? []) {
        sinceByStudent.set(m.student_id, m.joined_at)
      }
      for (const r of relationshipsRes.data ?? []) {
        const existing = sinceByStudent.get(r.student_id)
        if (!existing || new Date(r.started_at) < new Date(existing)) {
          sinceByStudent.set(r.student_id, r.started_at)
        }
        instructorByStudent.set(r.student_id, r.instructor_id)
        relationshipByStudent.set(r.student_id, {
          id: r.id,
          packageId: r.package_id,
          packageName: (r as any).school_packages?.name ?? null,
        })
      }

      const studentIds = [...sinceByStudent.keys()]
      if (studentIds.length === 0) { setStudents([]); return }

      const instructorIds = [...new Set(instructorByStudent.values())]
      const allProfileIds = [...new Set([...studentIds, ...instructorIds])]

      // Lesson counts (migration 092's "driving_lessons: school admin read" policy, same
      // boundary useSchoolInstructorCalendar relies on) — completed vs. still-scheduled,
      // per student, scoped to this school. Cancelled lessons count toward neither.
      const { data: lessonRows, error: lessonErr } = await supabase
        .from('driving_lessons')
        .select('student_id, status')
        .eq('school_id', selectedSchoolId)
        .in('student_id', studentIds)
        .in('status', ['completed', 'scheduled'])

      if (lessonErr) { setError('Could not load lesson counts.'); return }

      const completedByStudent = new Map<string, number>()
      const pendingByStudent = new Map<string, number>()
      for (const l of lessonRows ?? []) {
        const map = l.status === 'completed' ? completedByStudent : pendingByStudent
        map.set(l.student_id, (map.get(l.student_id) ?? 0) + 1)
      }

      // Public profiles. RLS "profiles: school admin read student" (migration 053,
      // widened in migration 074) allows this for a student reached by either path above;
      // "profiles: school admin read" (migration 050) covers the assigned instructor,
      // who is necessarily an active member of this same school's roster.
      const { data: profiles, error: profErr } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', allProfileIds)

      if (profErr) { setError('Could not load student profiles.'); return }

      const profileById = new Map((profiles ?? []).map(p => [p.id, p]))

      const result: SchoolStudentSummary[] = []
      for (const studentId of studentIds) {
        // A student id whose profile didn't come back (deleted between queries, or not
        // yet readable under RLS) is skipped rather than rendered with missing data.
        const p = profileById.get(studentId)
        if (!p) continue
        const instructorId = instructorByStudent.get(studentId) ?? null
        const rel = relationshipByStudent.get(studentId)
        result.push({
          studentId,
          joinedAt: sinceByStudent.get(studentId)!,
          fullName: p.full_name,
          email: p.email,
          instructorId,
          instructorName: instructorId ? (profileById.get(instructorId)?.full_name ?? null) : null,
          relationshipId: rel?.id ?? null,
          packageId: rel?.packageId ?? null,
          packageName: rel?.packageName ?? null,
          lessonsCompleted: completedByStudent.get(studentId) ?? 0,
          lessonsPending: pendingByStudent.get(studentId) ?? 0,
        })
      }

      result.sort((a, b) => new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime())
      setStudents(result)
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId])

  // Clear immediately on school change so the previous school's rows are never shown,
  // even briefly, while the new school's data is still loading.
  useEffect(() => { setStudents([]) }, [selectedSchoolId])
  useEffect(() => { fetchStudents() }, [fetchStudents])

  return { students, loading, error, refresh: fetchStudents }
}
